import { useState, useEffect, useRef } from "react";
import { X, Eye, Type, MousePointer2, Pause, RotateCcw, Contrast, ZoomIn } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * AccessibilityWidget - Minimal but practical accessibility features
 * Covers basic legal requirements for accessibility compliance
 */
export const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    highContrast: false,
    highlightLinks: false,
    bigCursor: false,
    pauseAnimations: false,
    lineHeight: 100,
  });
  const { language } = useLanguage();
  const widgetRef = useRef(null);
  const timerRef = useRef(null);

  // Auto-minimize after 15 seconds - with proper cleanup
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!isOpen && !isMinimized) {
      timerRef.current = setTimeout(() => {
        setIsMinimized(true);
      }, 15000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isOpen, isMinimized]);

  // Reset minimized state when opening widget
  useEffect(() => {
    if (isOpen) {
      setIsMinimized(false);
    }
  }, [isOpen]);

  const t = {
    he: {
      title: "נגישות",
      reset: "איפוס",
      fontSize: "גודל טקסט",
      highContrast: "ניגודיות גבוהה",
      highlightLinks: "הדגשת קישורים",
      bigCursor: "סמן גדול",
      pauseAnimations: "עצירת אנימציות",
      lineHeight: "מרווח שורות",
      increase: "הגדל",
      decrease: "הקטן",
      statement: "הצהרת נגישות",
    },
    en: {
      title: "Accessibility",
      reset: "Reset",
      fontSize: "Font Size",
      highContrast: "High Contrast",
      highlightLinks: "Highlight Links",
      bigCursor: "Large Cursor",
      pauseAnimations: "Pause Animations",
      lineHeight: "Line Height",
      increase: "Increase",
      decrease: "Decrease",
      statement: "Accessibility Statement",
    }
  };

  const labels = t[language] || t.he;

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    
    root.style.fontSize = `${settings.fontSize}%`;
    
    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    if (settings.highlightLinks) {
      root.classList.add('highlight-links');
    } else {
      root.classList.remove('highlight-links');
    }
    
    if (settings.bigCursor) {
      root.classList.add('big-cursor');
    } else {
      root.classList.remove('big-cursor');
    }
    
    if (settings.pauseAnimations) {
      root.classList.add('pause-animations');
    } else {
      root.classList.remove('pause-animations');
    }
    
    root.style.setProperty('--accessibility-line-height', `${settings.lineHeight}%`);
    
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
  }, [settings]);

  // Load settings from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('accessibility-settings');
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load accessibility settings');
      }
    }
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const resetSettings = () => {
    setSettings({
      fontSize: 100,
      highContrast: false,
      highlightLinks: false,
      bigCursor: false,
      pauseAnimations: false,
      lineHeight: 100,
    });
  };

  const adjustValue = (key, delta, min, max) => {
    setSettings(prev => ({
      ...prev,
      [key]: Math.min(max, Math.max(min, prev[key] + delta))
    }));
  };

  const toggleSetting = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div ref={widgetRef} className="fixed bottom-6 right-6 z-50 lg:bottom-auto lg:top-28 lg:right-6" dir="ltr">
      {/* Floating Button with Animation */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setIsMinimized(false);
          }
        }}
        aria-label={labels.title}
        aria-expanded={isOpen}
        data-testid="accessibility-toggle"
        data-minimized={isMinimized}
        className={`
          group relative overflow-hidden
          rounded-full shadow-lg
          focus:outline-none focus:ring-4 focus:ring-[#4A5D43]/30
          transition-all duration-500 ease-out
          flex items-center justify-center
          ${isOpen 
            ? 'w-11 h-11' 
            : isMinimized
              ? 'w-11 h-11 hover:scale-110 hover:shadow-xl'
              : 'px-4 py-2.5 hover:scale-105 hover:shadow-xl'
          }
        `}
        style={{
          background: 'linear-gradient(135deg, #4A5D43 0%, #3A4A35 50%, #2C3329 100%)',
          backgroundSize: '200% 200%',
          animation: isOpen ? 'none' : 'gradientShift 3s ease infinite',
        }}
      >
        {/* Animated ring effect */}
        {!isOpen && !isMinimized && (
          <span className="absolute inset-0 rounded-full animate-ping opacity-20 bg-white" style={{ animationDuration: '2s' }} />
        )}
        
        {/* Pulse glow effect on hover */}
        <span 
          className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            boxShadow: '0 0 25px rgba(74, 93, 67, 0.6), 0 0 50px rgba(74, 93, 67, 0.4)',
          }}
        />
        
        {/* Content */}
        <span className="relative z-10 flex items-center justify-center">
          {isOpen ? (
            <X className="h-5 w-5 text-white" />
          ) : (
            <>
              {/* Accessibility Icon */}
              <svg 
                className={`h-5 w-5 shrink-0 transition-transform duration-300 text-white ${!isMinimized ? 'group-hover:scale-110' : ''}`} 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <circle cx="12" cy="4" r="2"/>
                <path d="M12 6v4"/>
                <path d="M8 10h8"/>
                <path d="M10 14l-2 6"/>
                <path d="M14 14l2 6"/>
                <path d="M12 10v4"/>
              </svg>
              
              {/* Label - visible when not minimized - ALWAYS WHITE */}
              <span 
                className={`text-sm font-semibold whitespace-nowrap overflow-hidden transition-all duration-500 text-white ${
                  isMinimized ? 'w-0 opacity-0 mr-0' : 'w-auto opacity-100 mr-2'
                }`}
                style={{ color: '#FFFFFF', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
              >
                {language === 'he' ? 'נגישות' : 'Accessibility'}
              </span>
            </>
          )}
        </span>
      </button>

      {/* Panel with Dark Mode Support */}
      {isOpen && (
        <div 
          className="absolute w-72 bg-white dark:bg-[#1a231a] rounded-2xl shadow-2xl border border-[#E8E4DC] dark:border-[#2a3a2a] overflow-hidden animate-fade-in-up bottom-16 right-0 lg:top-14 lg:bottom-auto lg:right-0"
          role="dialog"
          aria-label={labels.title}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#4A5D43] to-[#5D7356] text-white px-4 py-3 flex items-center justify-between">
            <h3 className="font-medium flex items-center gap-2">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="4" r="2"/>
                <path d="M12 6v4"/>
                <path d="M8 10h8"/>
                <path d="M10 14l-2 6"/>
                <path d="M14 14l2 6"/>
                <path d="M12 10v4"/>
              </svg>
              {labels.title}
            </h3>
            <button 
              onClick={resetSettings}
              className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded-full flex items-center gap-1 transition-colors"
              aria-label={labels.reset}
            >
              <RotateCcw className="h-3 w-3" />
              {labels.reset}
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 max-h-80 overflow-y-auto">
            {/* Font Size */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2C3329] dark:text-[#E8E4DC] flex items-center gap-2">
                <Type className="h-4 w-4 text-[#4A5D43] dark:text-[#7DAA6B]" />
                {labels.fontSize}: {settings.fontSize}%
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => adjustValue('fontSize', -10, 80, 150)}
                  className="flex-1 py-2 bg-[#F5F2EB] dark:bg-[#2a3a2a] hover:bg-[#E8E4DC] dark:hover:bg-[#3a4a3a] rounded-lg text-sm font-medium text-[#2C3329] dark:text-[#E8E4DC] transition-colors"
                  aria-label={labels.decrease}
                >
                  A-
                </button>
                <button
                  onClick={() => adjustValue('fontSize', 10, 80, 150)}
                  className="flex-1 py-2 bg-[#F5F2EB] dark:bg-[#2a3a2a] hover:bg-[#E8E4DC] dark:hover:bg-[#3a4a3a] rounded-lg text-sm font-medium text-[#2C3329] dark:text-[#E8E4DC] transition-colors"
                  aria-label={labels.increase}
                >
                  A+
                </button>
              </div>
            </div>

            {/* Line Height */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2C3329] dark:text-[#E8E4DC] flex items-center gap-2">
                <ZoomIn className="h-4 w-4 text-[#4A5D43] dark:text-[#7DAA6B]" />
                {labels.lineHeight}: {settings.lineHeight}%
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => adjustValue('lineHeight', -10, 100, 200)}
                  className="flex-1 py-2 bg-[#F5F2EB] dark:bg-[#2a3a2a] hover:bg-[#E8E4DC] dark:hover:bg-[#3a4a3a] rounded-lg text-sm font-medium text-[#2C3329] dark:text-[#E8E4DC] transition-colors"
                >
                  -
                </button>
                <button
                  onClick={() => adjustValue('lineHeight', 10, 100, 200)}
                  className="flex-1 py-2 bg-[#F5F2EB] dark:bg-[#2a3a2a] hover:bg-[#E8E4DC] dark:hover:bg-[#3a4a3a] rounded-lg text-sm font-medium text-[#2C3329] dark:text-[#E8E4DC] transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            {/* Toggle Options */}
            <div className="space-y-2">
              {/* High Contrast */}
              <button
                onClick={() => toggleSetting('highContrast')}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                  settings.highContrast 
                    ? 'bg-[#4A5D43] text-white' 
                    : 'bg-[#F5F2EB] dark:bg-[#2a3a2a] hover:bg-[#E8E4DC] dark:hover:bg-[#3a4a3a] text-[#2C3329] dark:text-[#E8E4DC]'
                }`}
                aria-pressed={settings.highContrast}
              >
                <span className="flex items-center gap-2 text-sm">
                  <Contrast className="h-4 w-4" />
                  {labels.highContrast}
                </span>
                <span className={`w-4 h-4 rounded-full border-2 ${
                  settings.highContrast ? 'bg-white border-white' : 'border-[#9A9790] dark:border-[#6B7266]'
                }`} />
              </button>

              {/* Highlight Links */}
              <button
                onClick={() => toggleSetting('highlightLinks')}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                  settings.highlightLinks 
                    ? 'bg-[#4A5D43] text-white' 
                    : 'bg-[#F5F2EB] dark:bg-[#2a3a2a] hover:bg-[#E8E4DC] dark:hover:bg-[#3a4a3a] text-[#2C3329] dark:text-[#E8E4DC]'
                }`}
                aria-pressed={settings.highlightLinks}
              >
                <span className="flex items-center gap-2 text-sm">
                  <Eye className="h-4 w-4" />
                  {labels.highlightLinks}
                </span>
                <span className={`w-4 h-4 rounded-full border-2 ${
                  settings.highlightLinks ? 'bg-white border-white' : 'border-[#9A9790] dark:border-[#6B7266]'
                }`} />
              </button>

              {/* Big Cursor */}
              <button
                onClick={() => toggleSetting('bigCursor')}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                  settings.bigCursor 
                    ? 'bg-[#4A5D43] text-white' 
                    : 'bg-[#F5F2EB] dark:bg-[#2a3a2a] hover:bg-[#E8E4DC] dark:hover:bg-[#3a4a3a] text-[#2C3329] dark:text-[#E8E4DC]'
                }`}
                aria-pressed={settings.bigCursor}
              >
                <span className="flex items-center gap-2 text-sm">
                  <MousePointer2 className="h-4 w-4" />
                  {labels.bigCursor}
                </span>
                <span className={`w-4 h-4 rounded-full border-2 ${
                  settings.bigCursor ? 'bg-white border-white' : 'border-[#9A9790] dark:border-[#6B7266]'
                }`} />
              </button>

              {/* Pause Animations */}
              <button
                onClick={() => toggleSetting('pauseAnimations')}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                  settings.pauseAnimations 
                    ? 'bg-[#4A5D43] text-white' 
                    : 'bg-[#F5F2EB] dark:bg-[#2a3a2a] hover:bg-[#E8E4DC] dark:hover:bg-[#3a4a3a] text-[#2C3329] dark:text-[#E8E4DC]'
                }`}
                aria-pressed={settings.pauseAnimations}
              >
                <span className="flex items-center gap-2 text-sm">
                  <Pause className="h-4 w-4" />
                  {labels.pauseAnimations}
                </span>
                <span className={`w-4 h-4 rounded-full border-2 ${
                  settings.pauseAnimations ? 'bg-white border-white' : 'border-[#9A9790] dark:border-[#6B7266]'
                }`} />
              </button>
            </div>
          </div>

          {/* Footer - Accessibility Statement Link */}
          <div className="px-4 py-3 bg-[#F5F2EB] dark:bg-[#151d15] border-t border-[#E8E4DC] dark:border-[#2a3a2a]">
            <a 
              href="/terms#accessibility" 
              className="text-xs text-[#4A5D43] dark:text-[#7DAA6B] hover:underline flex items-center gap-1"
            >
              {labels.statement} →
            </a>
          </div>
        </div>
      )}

      {/* CSS for gradient animation */}
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
};

export default AccessibilityWidget;
